import { useState } from 'react';
import type { EnlaceRrpp } from '../../../api/ops/portalRrpp';
import { Boton } from '../../../componentes/Boton';
import { Ventana } from '../../../componentes/Ventana';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import type { PropsPantalla } from '../../tipos';
import { Caja, TarjetaEnlace } from '../comun';
import { useAvisarError } from '../uso';
import { diaHora, motivosDeEnlace, opsDe, sinProtocolo } from '../textos';

/**
 * Un enlace de RRPP (10.10): el de Instagram para su historia y el formulario web (G729). Sin Instagram conectado o sin
 * web publicada la dirección llega nula y `unavailable_reasons` dice por qué (G517, G675, G788). Regenerar (el viejo deja
 * de valer para siempre) y desactivar, con ventana; sobre uno desactivado, «Darle un enlace nuevo» también regenera (G788).
 */

export function EnlaceDeRrpp({ props, enlace, titulo, texto, nombre, puede, onCambio }: { props: PropsPantalla; enlace: EnlaceRrpp; titulo: string; texto?: string; nombre: string; puede: boolean; onCambio: (nuevo: EnlaceRrpp | null) => void }) {
    const { negocio, movil } = props;
    const ops = opsDe(props.ops);
    const avisar = useAvisoBreve();
    const avisarError = useAvisarError();
    const [ventana, setVentana] = useState<'regenerar' | 'desactivar' | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const vivo = enlace.status === 'active';
    const donde = enlace.kind === 'fixed' ? 'fijo' : 'del evento';
    const motivos = motivosDeEnlace(enlace);
    const filas = [
        ...(enlace.instagram_url == null ? [] : [{ k: enlace.kind === 'fixed' ? 'Instagram' : 'Instagram · para su historia', v: sinProtocolo(enlace.instagram_url), copiar: vivo }]),
        ...(enlace.web_form_url == null || !vivo ? [] : [{ k: 'Formulario web · sin Instagram', v: sinProtocolo(enlace.web_form_url) }]),
    ];
    const confirmar = () => {
        setOcupado(true);
        const p = ventana === 'desactivar' ? ops.deactivatePromoterLink(negocio, enlace.id) : ops.regeneratePromoterLink(negocio, enlace.id);
        p.then((nuevo) => {
            avisar({ tipo: 'ok', texto: ventana === 'desactivar' ? 'Enlace desactivado.' : `Enlace nuevo listo. ${nombre} lo ve ya en su portal: tiene que volver a publicarlo.` });
            setVentana(null);
            onCambio(nuevo);
        })
            .catch((e: unknown) => { avisarError(e); })
            .finally(() => { setOcupado(false); });
    };
    return (
        <>
            <TarjetaEnlace
                titulo={vivo ? titulo : 'Desactivado para siempre'}
                texto={vivo ? [texto, ...motivos].filter((x) => x !== undefined && x !== '').join(' · ') : `Desactivado${enlace.deactivated_at == null ? '' : ` el ${diaHora(enlace.deactivated_at)}`} · ${String(enlace.signups ?? 0)} apuntados con él siguen con su pase`}
                chip={vivo ? (enlace.usable_now === false ? ['Cerrado', 'neutral'] : ['Activo', 'confirmed']) : ['Desactivado', 'problem']}
                icono={enlace.kind === 'fixed' ? 'link' : 'instagram'}
                filas={filas}
                apagada={!vivo}
            >
                {vivo && filas.length === 0 && <Caja icono="instagram">Sus enlaces salen aquí cuando conectes la cuenta de Instagram del negocio y publiques tu web.</Caja>}
                {puede && (
                    <div className="rv-ls-botones__fila">
                        {vivo ? (
                            <>
                                <Boton tipo="secundario" icono="refresh-cw" onClick={() => { setVentana('regenerar'); }}>Regenerar</Boton>
                                <Boton tipo="destructivo" icono="ban" onClick={() => { setVentana('desactivar'); }}>Desactivar</Boton>
                            </>
                        ) : (
                            <Boton tipo="secundario" icono="refresh-cw" onClick={() => { setVentana('regenerar'); }}>Darle un enlace nuevo</Boton>
                        )}
                    </div>
                )}
            </TarjetaEnlace>
            <Ventana
                abierta={ventana !== null}
                titulo={ventana === 'desactivar' ? `Desactivar el enlace ${donde}` : `Regenerar el enlace ${donde}`}
                texto={
                    ventana === 'desactivar'
                        ? `Para siempre: no se puede volver a activar. Quien escriba con él recibirá «el enlace ya no es válido». Los ${String(enlace.signups ?? 0)} ya apuntados siguen con su pase.`
                        : vivo
                          ? `El enlace actual de ${nombre} deja de funcionar para siempre, también donde ya lo haya publicado o compartido: quien escriba con él recibirá «el enlace ya no es válido».`
                          : `${nombre} recibe un enlace nuevo ${donde}. El desactivado sigue sin valer y sus apuntados siguen con su pase.`
                }
                accion={ventana === 'desactivar' ? 'Desactivar' : 'Regenerar'}
                accionIcono={ventana === 'desactivar' ? 'ban' : 'refresh-cw'}
                ocupado={ocupado}
                ocupadoTexto={ventana === 'desactivar' ? 'Desactivando…' : 'Regenerando…'}
                movil={movil}
                onAccion={confirmar}
                onCerrar={() => { if (!ocupado) { setVentana(null); } }}
            >
                {ventana === 'desactivar' && <Caja icono="info">Si solo se ha filtrado, mejor «Regenerar»: {nombre} recibe uno nuevo al momento.</Caja>}
            </Ventana>
        </>
    );
}
